import { useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import { DateTime } from 'luxon';
import { usePopper } from 'react-popper';

import { Popover, Portal } from '@headlessui/react';
import {
  BellIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import { LoadingWheel } from '../base/LoadingWheel';
import { useIntersection } from '../../hooks/useIntersection';
import { useResettableTimer } from '../../utils/useResettableTimer';
import useUserNotifications from '../../data/user/useUserNotifications';
import { IPDNotification } from '../../types/PDNotification/PDNotification';
import useUserNotificationsMutations from '../../data/user/useUserNotificationsMutations';
import useCustomers from '../../data/customer/useCustomers';
import { Pill } from '../base/Pill';
import { useUserContext } from '../../contexts/UserContext';
import { useNavigate } from 'react-router';

interface NotificationListItemProps extends IPDNotification {
  onAcknowledge: (id: string) => void;
  onClose?: () => void;
}

function NotificationListItem({
  id,
  title,
  body,
  level,
  timestamp,
  acknowledgedOn,
  applianceId,
  faultDefinitionId,
  customerId,
  onAcknowledge,
  onClose,
}: NotificationListItemProps) {
  const navigate = useNavigate();
  const { isSuperAdmin, organisationId, superAdminId } = useUserContext();
  const { customers } = useCustomers();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const isVisible = useIntersection(triggerRef, '-100px');
  const isAcknowledged = acknowledgedOn !== null;
  const isCustomer = customerId !== null;

  useEffect(() => {
    console.log([
      isVisible,
      isAcknowledged,
      isSuperAdmin,
      organisationId === process.env.REACT_APP_PD_ORG_ID,
      superAdminId,
    ]);

    if (
      isVisible &&
      !isAcknowledged &&
      (!isSuperAdmin || organisationId === process.env.REACT_APP_PD_ORG_ID)
    ) {
      onAcknowledge(id);
      console.log('acking notification', id);
    } else {
      console.log('not acking notification', id);
    }
  }, [isVisible, isAcknowledged, isSuperAdmin, organisationId]);

  const getLevelColour = (level: number) => {
    switch (level) {
      case 0:
        return 'bg-blue-100 dark:bg-blue-900/60';
      case 1:
        return 'bg-green-100 dark:bg-green-900/60';
      case 2:
        return 'bg-amber-100 dark:bg-amber-900/60';
      case 3:
        return 'bg-red-100 dark:bg-red-900/60';
    }
  };

  const getLevelIcon = (level: number) => {
    const baseClasses = 'h-10 w-10 text-gray-700 dark:text-gray-300';

    switch (level) {
      case 0:
        return <BellIcon className={baseClasses} aria-hidden="true" />;
      case 1:
        return <CheckCircleIcon className={baseClasses} aria-hidden="true" />;
      case 2:
        return (
          <ExclamationTriangleIcon className={baseClasses} aria-hidden="true" />
        );
      case 3:
        return (
          <ExclamationCircleIcon className={baseClasses} aria-hidden="true" />
        );
    }
  };

  const timeAgo = (
    dateTime: DateTime | string | Date | null | undefined
  ): string => {
    if (!dateTime) return '';

    const dt =
      typeof dateTime === 'string' || dateTime instanceof Date
        ? DateTime.fromISO(
            dateTime instanceof Date ? dateTime.toISOString() : dateTime
          )
        : dateTime;

    const now = DateTime.now();
    const totalSeconds = now.diff(dt, 'seconds').seconds ?? 0;
    const totalMinutes = now.diff(dt, 'minutes').minutes ?? 0;
    const totalHours = now.diff(dt, 'hours').hours ?? 0;
    const totalDays = now.diff(dt, 'days').days ?? 0;

    if (totalSeconds < 60) {
      return 'just now';
    } else if (totalMinutes < 60) {
      const m = Math.floor(totalMinutes);
      return `${m} minute${m === 1 ? '' : 's'} ago`;
    } else if (totalHours < 24) {
      const h = Math.floor(totalHours);
      return `${h} hour${h === 1 ? '' : 's'} ago`;
    } else if (totalDays < 7) {
      const d = Math.floor(totalDays);
      return `${d} day${d === 1 ? '' : 's'} ago`;
    } else {
      const weeks = Math.floor(totalDays / 7);
      return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    }
  };

  const customerName = useMemo(() => {
    if (!isCustomer || !customers.data) return 'Unknown Customer';

    return customers.data.find((c) => c.id === customerId)?.organisationName;
  }, [customers.data, isCustomer, customerId]);

  return (
    <div
      ref={triggerRef}
      className={clsx(
        'w-96 relative',
        applianceId &&
          faultDefinitionId &&
          'hover:cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900'
      )}
      onClick={
        applianceId && faultDefinitionId
          ? () => {
              navigate(`/faults/${faultDefinitionId}`, {
                state: { applianceId },
              });
              onClose?.();
            }
          : () => {}
      }
    >
      {isAcknowledged && (
        <div className="absolute inset-0 bg-gray-300/40 z-10" />
      )}
      <div className="flex items-center p-4 space-x-4">
        <div>
          <div
            className={clsx(
              'rounded-full p-1 text-gray-900',
              getLevelColour(level)
            )}
          >
            {getLevelIcon(level)}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {body}
          </p>
          <div className="mt-2 flex items-center justify-between">
            <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
              {timeAgo(DateTime.fromISO(timestamp))}
            </p>
            {isCustomer ? (
              <Pill classes="bg-primary/60 text-primaryContrast">
                <span className="truncate max-w-[160px] block">
                  {customerName}
                </span>
              </Pill>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface NotificationPopoverProps {
  buttonContainerClasses?: string;
}

export function NotificationPopover({
  buttonContainerClasses,
}: NotificationPopoverProps) {
  const { userNotifications } = useUserNotifications();
  const { acknowledge } = useUserNotificationsMutations();
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement>();
  const [popperElement, setPopperElement] = useState<HTMLDivElement>();
  const notificationsToAcknowledge = useRef<string[]>([]);
  const { styles, attributes } = usePopper(referenceElement, popperElement);
  const { start, reset, isRunning } = useResettableTimer(
    () =>
      acknowledge.mutate(notificationsToAcknowledge.current, {
        onSettled: () => {
          notificationsToAcknowledge.current = [];
        },
      }),
    2000
  );

  const avgItemHeight = 80; // Approximate height of each notification item
  const noVisableItems = 6; // Number of items to show before scrolling
  const maxHeight = avgItemHeight * noVisableItems;

  const handleAcknowledge = (notificationId: string) => {
    notificationsToAcknowledge.current = [
      ...notificationsToAcknowledge.current,
      notificationId,
    ];

    if (!isRunning) {
      start();
    } else {
      reset();
    }
  };

  const unreadCount = useMemo(() => {
    if (!userNotifications.data) return 0;

    return userNotifications.data.filter(
      (notification) => !notification.acknowledgedOn
    ).length;
  }, [userNotifications.data]);

  return (
    <Popover className="relative -mb-1">
      {({ close }) => (
        <>
          <Popover.Button
            className={({ open }) =>
              clsx(
                buttonContainerClasses,
                open && 'z-0 outline-none ring-1 ring-primaryContrast'
              )
            }
            ref={(r) => setReferenceElement(r!)}
          >
            <div>
              <span className="sr-only">View notifications</span>
              <BellIcon className="h-6 w-6" aria-hidden="true" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-500 text-white text-xs font-medium">
                  {unreadCount}
                </span>
              )}
            </div>
          </Popover.Button>
          <Popover.Panel
            ref={(r) => setPopperElement(r!)}
            style={{ ...styles.popper, zIndex: 50 }}
            {...attributes.popper}
          >
            <div
              style={{ maxHeight: maxHeight }}
              className="overflow-auto mt-2 bg-paper rounded-lg shadow-lg divide-y divide-gray-200 dark:divide-gray-700"
            >
              {userNotifications.isLoading ? (
                <div className="flex items-center justify-center py-10">
                  <LoadingWheel />
                </div>
              ) : userNotifications.data?.length === 0 ? (
                <div className="w-96 p-4 text-center text-gray-500 dark:text-gray-400">
                  No notifications
                </div>
              ) : (
                userNotifications.data
                  ?.sort(
                    (a, b) =>
                      DateTime.fromISO(b.timestamp).toMillis() -
                      DateTime.fromISO(a.timestamp).toMillis()
                  )
                  .map((notification) => (
                    <NotificationListItem
                      onAcknowledge={handleAcknowledge}
                      onClose={close}
                      key={notification.id}
                      {...notification}
                    />
                  ))
              )}
            </div>
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
}
